// CHI Dashboard -- per-site health matrix (Phase 3). One row per
// Active site, sortable, filterable by owner + fuel brand. Uses the
// /api/chi/sites endpoint.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ChiBadge from '../components/ChiBadge.jsx';

function bucketFor(s) {
  if (s === null || s === undefined) return 'grey';
  if (s >= 80) return 'green';
  if (s >= 50) return 'amber';
  return 'red';
}

export default function ChiDashboard() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [brandFilter, setBrandFilter] = useState('all');
  // Bucket filter for the Green/Amber/Red counter chips. 'all' shows
  // everything, otherwise filters to sites in that health bucket.
  // Dave 2026-07-09: make the counter chips clickable so he can jump
  // straight to (e.g.) the 33 red sites without hunting.
  const [bucketFilter, setBucketFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('shi_score');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    axios.get('/api/chi/sites').then((r) => {
      setSites(r.data.sites || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const owners = useMemo(() => {
    const s = new Set(sites.map((x) => x.owner_name).filter(Boolean));
    return ['all', ...Array.from(s).sort()];
  }, [sites]);
  const brands = useMemo(() => {
    const s = new Set(sites.map((x) => x.fuel_brand).filter(Boolean));
    return ['all', ...Array.from(s).sort()];
  }, [sites]);

  const filtered = useMemo(() => {
    return sites.filter((s) => {
      if (ownerFilter !== 'all' && s.owner_name !== ownerFilter) return false;
      if (brandFilter !== 'all' && s.fuel_brand !== brandFilter) return false;
      if (bucketFilter !== 'all' && bucketFor(s.shi_score) !== bucketFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${s.site_number || ''} ${s.site_name || ''} ${s.company || ''} ${s.city || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sites, ownerFilter, brandFilter, bucketFilter, search]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      const cmp = String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  // Counts for the Green/Amber/Red chips. Uses the site set with owner/
  // brand/search applied but NOT bucket -- otherwise clicking Red would
  // zero out the Green + Amber counts on the same row, which reads as a
  // bug even though the numbers would be technically correct.
  const distributionBase = useMemo(() => {
    return sites.filter((s) => {
      if (ownerFilter !== 'all' && s.owner_name !== ownerFilter) return false;
      if (brandFilter !== 'all' && s.fuel_brand !== brandFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const hay = `${s.site_number || ''} ${s.site_name || ''} ${s.company || ''} ${s.city || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [sites, ownerFilter, brandFilter, search]);
  const dist = useMemo(() => {
    const d = { green: 0, amber: 0, red: 0, grey: 0 };
    for (const s of distributionBase) d[bucketFor(s.shi_score)]++;
    return d;
  }, [distributionBase]);

  const avg = useMemo(() => {
    const scored = filtered.filter((s) => s.shi_score !== null);
    if (scored.length === 0) return 0;
    return Math.round(scored.reduce((sum, s) => sum + s.shi_score, 0) / scored.length);
  }, [filtered]);

  function toggleSort(key) {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }
  function sortIcon(key) {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  if (loading) return <section className="page"><h2>CHI Dashboard</h2><p>Loading…</p></section>;

  return (
    <section className="page">
      <div className="page-header">
        <h2>CHI Dashboard <span style={{ fontSize: 12, color: 'var(--color-text-dim)' }}>Site Health Index — one row per active site</span></h2>
      </div>

      {/* Signal legend -- explains what each column measures + max points */}
      <details style={{
        marginBottom: 'var(--space-4)',
        background: 'var(--color-surface-sunken, #f8fafc)',
        border: '1px solid var(--color-border, #e5e7eb)',
        borderRadius: 6,
        padding: 'var(--space-3)',
      }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
          What does each score mean? <span style={{ color: 'var(--color-text-dim)', fontWeight: 400 }}>(click to expand)</span>
        </summary>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-3)',
          fontSize: 12,
        }}>
          <div>
            <strong>Recon</strong> <span style={{ color: 'var(--color-text-dim)' }}>/ 30</span><br />
            Fuel day-end recency. Full points if reconciled within 7 days; decays to 0 by 45 days. The heaviest weight -- it's the daily habit that proves they're actually using Fuel Manager.
            <br /><span style={{ color: 'var(--color-text-dim)', fontStyle: 'italic' }}>Shows <code>N/S</code> for sites without Fuel Manager (signal excluded from score).</span>
          </div>
          <div>
            <strong>Login</strong> <span style={{ color: 'var(--color-text-dim)' }}>/ 25</span><br />
            Last xsiteio login. Full points if within 7 days; decays to 0 by 60 days. If nobody's logging in, nobody's using it.
          </div>
          <div>
            <strong>Update</strong> <span style={{ color: 'var(--color-text-dim)' }}>/ 15</span><br />
            Last data update (price book etc.). Full points if within 14 days; decays to 0 by 90 days. Less frequent by design -- pricebook maintenance is weekly, not daily.
          </div>
          <div>
            <strong>FM</strong> <span style={{ color: 'var(--color-text-dim)' }}>/ 15</span><br />
            Fuel Manager subscription. 15 if they're on an active FM plan.
            <br /><span style={{ color: 'var(--color-text-dim)', fontStyle: 'italic' }}>Shows <code>N/S</code> for non-FM sites -- both this and Recon are excluded and the remaining 4 signals (Login/Update/Renewal/xsio) get normalized to the same 100-point scale, so Price-Book-only sites aren't unfairly penalized. Missing FM is still a Fuel-Manager upsell opportunity, just not a health-index one.</span>
          </div>
          <div>
            <strong>Renewal</strong> <span style={{ color: 'var(--color-text-dim)' }}>/ 10</span><br />
            Contract cycle position. Full 10 mid-year (2-10 months out); 4 in last 60 days (churn window); 7 just after renewal (monitor); 0 if renewal date passed. When money is at risk.
          </div>
          <div>
            <strong>xsio</strong> <span style={{ color: 'var(--color-text-dim)' }}>/ 5</span><br />
            xsiteio Active flag. 5 if xsiteio is currently billing them, 0 if inactive. Zero here = local Active but xsiteio not billing -- money leak or onboarding gap.
          </div>
        </div>
        <div style={{
          marginTop: 'var(--space-3)', paddingTop: 'var(--space-3)',
          borderTop: '1px solid var(--color-border, #e5e7eb)',
          fontSize: 12, color: 'var(--color-text-dim)',
        }}>
          <strong>SHI total</strong>: sum of all six / 100 for Fuel Manager sites. For sites without Fuel Manager, the Recon + FM signals are excluded and the remaining 4 signals (Login/Update/Renewal/xsio) are normalized to a 100-point scale. Either way -- <strong>Green</strong> 80-100 (healthy), <strong>Amber</strong> 50-79 (watch), <strong>Red</strong> 0-49 (at risk).
        </div>
      </details>

      {/* Distribution strip */}
      <div style={{
        display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-4)',
        alignItems: 'center', flexWrap: 'wrap',
      }}>
        <div><strong>{filtered.length}</strong> sites  |  avg <strong>{avg}</strong></div>
        {/* Clickable filter chips. Click toggles the bucket filter on/off.
            Active chip gets a bold border + the "clear" affordance in the
            label so it's obvious which bucket is currently in view. */}
        {[
          { key: 'green', label: 'Green', bg: '#d1fae5', fg: '#047857', border: '#047857', count: dist.green },
          { key: 'amber', label: 'Amber', bg: '#fef3c7', fg: '#b45309', border: '#b45309', count: dist.amber },
          { key: 'red',   label: 'Red',   bg: '#fee2e2', fg: '#b91c1c', border: '#b91c1c', count: dist.red   },
        ].map((chip) => {
          const isActive = bucketFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => setBucketFilter(isActive ? 'all' : chip.key)}
              title={isActive ? `Clear ${chip.label} filter` : `Show only ${chip.label} sites`}
              style={{
                background: chip.bg,
                color: chip.fg,
                padding: '4px 10px',
                borderRadius: 4,
                fontWeight: 700,
                cursor: 'pointer',
                border: `2px solid ${isActive ? chip.border : 'transparent'}`,
                boxShadow: isActive ? `0 0 0 2px ${chip.bg}` : 'none',
                fontFamily: 'inherit',
                fontSize: 'inherit',
              }}
            >
              {chip.label}: {chip.count}{isActive && ' x'}
            </button>
          );
        })}
        {dist.grey > 0 && (
          <button
            type="button"
            onClick={() => setBucketFilter(bucketFilter === 'grey' ? 'all' : 'grey')}
            title={bucketFilter === 'grey' ? 'Clear No data filter' : 'Show only sites with no health data'}
            style={{
              background: '#f3f4f6',
              color: '#6b7280',
              padding: '4px 10px',
              borderRadius: 4,
              fontWeight: 700,
              cursor: 'pointer',
              border: `2px solid ${bucketFilter === 'grey' ? '#6b7280' : 'transparent'}`,
              fontFamily: 'inherit',
              fontSize: 'inherit',
            }}
          >
            No data: {dist.grey}{bucketFilter === 'grey' && ' x'}
          </button>
        )}
        {bucketFilter !== 'all' && (
          <button
            type="button"
            onClick={() => setBucketFilter('all')}
            style={{
              padding: '4px 10px',
              borderRadius: 4,
              fontWeight: 500,
              cursor: 'pointer',
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              color: 'var(--color-text-dim)',
              fontFamily: 'inherit',
              fontSize: 'inherit',
            }}
          >
            Clear filter
          </button>
        )}
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
        <input
          placeholder="Search site # / name / company / city..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 250, padding: '6px 10px' }}
        />
        <select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
          {owners.map((o) => <option key={o} value={o}>{o === 'all' ? 'All owners' : o}</option>)}
        </select>
        <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
          {brands.map((b) => <option key={b} value={b}>{b === 'all' ? 'All brands' : b}</option>)}
        </select>
      </div>

      {/* Matrix */}
      <div style={{ overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 4 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: 'var(--color-surface-sunken)' }}>
            <tr>
              <th style={{ padding: '8px 10px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('site_number')}>Site #{sortIcon('site_number')}</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('site_name')}>Site Name{sortIcon('site_name')}</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('company')}>Customer{sortIcon('company')}</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('fuel_brand')}>Fuel{sortIcon('fuel_brand')}</th>
              <th style={{ padding: '8px 10px', textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('shi_score')}>SHI{sortIcon('shi_score')}</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }} title="Recon (30)">Recon</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }} title="Login (25)">Login</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }} title="Update (15)">Update</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }} title="Fuel Manager (15)">FM</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }} title="Renewal cycle (10)">Renewal</th>
              <th style={{ padding: '8px 10px', textAlign: 'center' }} title="xsiteio Active (5)">xsio</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('owner_name')}>Owner{sortIcon('owner_name')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((s) => (
              <tr key={s.site_id} style={{ borderTop: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--font-mono)' }}>{s.site_number || '—'}</td>
                <td style={{ padding: '6px 10px' }}>{s.site_name || '—'}</td>
                <td style={{ padding: '6px 10px' }}>
                  <Link to={`/customers/${s.customer_id}`}>{s.company}</Link>
                </td>
                <td style={{ padding: '6px 10px' }}>{s.fuel_brand || '—'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'center' }}><ChiBadge score={s.shi_score} /></td>
                {/* Recon + FM are Fuel Manager-only signals. Sites that
                    don't subscribe to FM show "N/S" (Not Subscribed)
                    instead of a bogus 0, and the shi_score is already
                    normalized on the 4 remaining signals so the badge
                    to the left reflects a fair health picture. */}
                <td style={{ padding: '6px 10px', textAlign: 'center', fontFamily: 'var(--font-mono)', color: s.has_fuel_manager === false ? 'var(--color-text-faint)' : undefined }}
                    title={s.has_fuel_manager === false ? 'Not subscribed to Fuel Manager -- signal excluded from score' : 'Days since last fuel recon'}>
                  {s.has_fuel_manager === false ? 'N/S' : (s.recon_score ?? '—')}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{s.login_score ?? '—'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{s.update_score ?? '—'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'center', fontFamily: 'var(--font-mono)', color: s.has_fuel_manager === false ? 'var(--color-text-faint)' : undefined }}
                    title={s.has_fuel_manager === false ? 'Not subscribed to Fuel Manager -- signal excluded from score' : 'Fuel Manager subscription active'}>
                  {s.has_fuel_manager === false ? 'N/S' : (s.fm_score ?? '—')}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{s.renewal_score ?? '—'}</td>
                <td style={{ padding: '6px 10px', textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{s.xsio_active_score ?? '—'}</td>
                <td style={{ padding: '6px 10px', color: 'var(--color-text-dim)' }}>{s.owner_name || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
